import { defineStore } from 'pinia'
import { sseService } from '../services/sseService.js'
import { useUserStore } from './userStore.js'

export const useNotificationStore = defineStore('notifications', {
  state: () => ({
    notifications: [
      {
        id: 1,
        type: 'info',
        category: 'games',
        message: 'New game added: Lakers vs Celtics',
        details: {
          gameId: '22201',
          teams: {
            home: 'Lakers',
            away: 'Celtics'
          }
        },
        time: '2 min ago',
        read: true
      },
      {
        id: 2,
        type: 'success',
        category: 'purchases',
        message: 'New game purchase',
        details: {
          gameId: '22201',
          client: {
            id: 'CLT001',
            name: 'John Smith',
            email: 'john@example.com'
          },
          game: {
            teams: {
              home: 'Lakers',
              away: 'Celtics'
            },
            league: 'NBA',
            startTime: '2024-12-10 18:30:00',
            price: 49.99
          },
          purchaseTime: '2024-12-06 18:58:06'
        },
        time: '5 min ago',
        read: true
      },
      {
        id: 3,
        type: 'info',
        category: 'games',
        message: 'Market update: LeBron James',
        details: {
          gameId: '22201',
          player: 'LeBron James',
          markets: [
            { type: 'Points', old: 25.5, new: 26.5, trend: 'up' },
            { type: 'Rebounds', old: 7.5, new: 8.5, trend: 'up' },
            { type: 'Assists', old: 8.5, new: 7.5, trend: 'down' }
          ]
        },
        time: '10 min ago',
        read: true
      }
    ],
    updates: [
      {
        id: 'upd-001',
        title: 'New Publish Flow',
        message: 'Published fixtures show a persistent badge and disable re-publish.',
        time: 'Just now',
        read: true
      }
    ],
    // SSE connection status
    sseConnected: false,
    sseLastMessageId: 0,
    // Auto-show notifications when new SSE messages arrive
    autoShowNotifications: true
  }),

  getters: {
    unreadCount: (state) => state.notifications.filter(n => !n.read).length,
    
    unreadNotifications: (state) => 
      state.notifications.filter(n => !n.read),
    
    sortedNotifications: (state) => 
      [...state.notifications].sort((a, b) => {
        if (a.read === b.read) return 0
        return a.read ? 1 : -1
      }),

    isSSEConnected: (state) => state.sseConnected
  },

  actions: {
    addUpdate(update) {
      this.updates.unshift({
        id: `upd-${Date.now()}`,
        read: false,
        time: 'Just now',
        ...update
      })
    },
    addNotification(notification) {
      // Calculate specials: progress is only on Balanced Lines (SSE); never list/toast here.
      if (notification?.category === 'specials') {
        return
      }
      this.notifications.unshift({
        id: Date.now(),
        read: false,
        time: 'Just now',
        type: 'info', // Default type
        ...notification
      })
    },

    markAsRead(notificationId) {
      const notification = this.notifications.find(n => n.id === notificationId)
      if (notification) {
        notification.read = true
      }
    },

    markAllAsRead() {
      this.notifications.forEach(n => n.read = true)
    },

    removeNotification(notificationId) {
      this.notifications = this.notifications.filter(n => n.id !== notificationId)
    },

    clearAllNotifications() {
      this.notifications = []
    },

    // SSE Integration Methods
    async initializeSSE() {
      console.log('SSE: Starting initialization...')
      try {
        // Subscribe to SSE connection status changes
        sseService.onConnectionStatus((connected) => {
          this.sseConnected = connected
          console.log('SSE: Connection status changed:', connected)
        })

        // Subscribe to SSE messages - listen to all types except system messages
        sseService.subscribe('*', (data, eventId) => {
          console.log('SSE: Received message in store:', data.type, data)
          // Skip system messages (connection, heartbeat)
          if (data.type === 'connection' || data.type === 'heartbeat') {
            console.log('SSE: System message received:', data.type)
            return
          }

          /*
           * -------------------------------------------------------------------------
           * Specials — notification center OFF (fully excluded)
           * Recalculation progress / tab refresh: BalancedLinesTab.vue + SpecialsTab.vue
           * subscribe to sseService directly; nothing here should add a toast/row.
           * -------------------------------------------------------------------------
           */
          if (data.published_by === 'specials-recalc' || data.category === 'specials') {
            return
          }

          // Handle actual notification messages
          console.log('SSE: Processing notification message:', data)
          this.handleSSEMessage(data, eventId)
        })

        // Connect to SSE stream with retry logic
        let retries = 0
        const maxRetries = 3
        
        while (retries < maxRetries) {
          try {
            console.log(`SSE: Attempting connection (attempt ${retries + 1}/${maxRetries})`)
            await sseService.connect()
            console.log('SSE: Successfully initialized and connected')
            return
          } catch (error) {
            retries++
            console.error(`SSE: Failed to initialize (attempt ${retries}/${maxRetries}):`, error)
            
            if (retries < maxRetries) {
              // Wait before retrying
              console.log(`SSE: Waiting ${2000 * retries}ms before retry...`)
              await new Promise(resolve => setTimeout(resolve, 2000 * retries))
            }
          }
        }
        
        console.error('SSE: Failed to initialize after all retries')
      } catch (error) {
        console.error('SSE: Failed to initialize:', error)
      }
    },

    handleSSEMessage(data, eventId) {
      console.log('SSE: handleSSEMessage called with:', data)

      const targetUserId = data.target_user_id ?? data.data?.user_id
      if (targetUserId != null && targetUserId !== '') {
        const userStore = useUserStore()
        const me = userStore.currentUser?.id
        if (me == null || String(me) !== String(targetUserId)) {
          console.log('SSE: Skipping notification (target_user_id does not match current user)')
          return
        }
      }

      // Update last message ID
      this.sseLastMessageId = eventId || data.id || 0

      // Handle games notifications separately
      if (data.type === 'schedule-result' || data.type === 'games-available') {
        // Import games notification store dynamically to avoid circular dependencies
        import('../stores/gamesNotificationStore.js').then(({ useGamesNotificationStore }) => {
          const gamesStore = useGamesNotificationStore()
          // Games can be in data.games or data.data.games (depending on SSE message structure)
          const games = data.games || (data.data && data.data.games) || []
          if (Array.isArray(games) && games.length > 0) {
            console.log('SSE: Adding games to notification store:', games.length)
            gamesStore.addGames(games)
          } else {
            console.log('SSE: No games found in schedule-result message', data)
          }
        })
        return
      }

      // Convert SSE message to notification format
      const notification = this.convertSSEMessageToNotification(data)
      console.log('SSE: Converted to notification:', notification)
      
      if (notification) {
        console.log('SSE: Adding notification to store')
        this.addNotification(notification)
        console.log('SSE: Notification added. Total notifications:', this.notifications.length)
      } else {
        console.log('SSE: No notification created from message')
      }
    },

    convertSSEMessageToNotification(sseMessage) {
      // Map SSE message types to notification types
      const typeMapping = {
        'message': 'info',
        'info': 'info',
        'success': 'success',
        'warning': 'warning',
        'error': 'error',
        'progress': 'progress',
        'notification': 'info'
      }

      // Determine notification type
      const notificationType = typeMapping[sseMessage.type] || 'info'

      // Format timestamp
      const timestamp = sseMessage.timestamp || new Date().toISOString()
      const timeAgo = this.formatTimeAgo(timestamp)

      const notification = {
        id: sseMessage.id || Date.now(),
        type: notificationType || 'info', // Ensure type is always defined
        category: sseMessage.category || 'system',
        message: sseMessage.message || 'New notification',
        details: sseMessage.data || {},
        time: timeAgo,
        read: false,
        source: 'sse',
        timestamp: timestamp
      }

      // Add progress field if it exists
      if (sseMessage.progress !== undefined) {
        notification.progress = sseMessage.progress
      }

      return notification
    },

    formatTimeAgo(timestamp) {
      const now = new Date()
      const time = new Date(timestamp)
      const diffInSeconds = Math.floor((now - time) / 1000)

      // Format the exact time for display
      const exactTime = time.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      })

      if (diffInSeconds < 60) {
        return `Just now (${exactTime})`
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60)
        return `${minutes} min ago (${exactTime})`
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600)
        return `${hours} hour${hours > 1 ? 's' : ''} ago (${exactTime})`
      } else {
        const days = Math.floor(diffInSeconds / 86400)
        return `${days} day${days > 1 ? 's' : ''} ago (${exactTime})`
      }
    },

    async publishSSEMessage(messageData) {
      try {
        // Use the correct API endpoint for publishing
        const result = await sseService.publishMessage(messageData, import.meta.env.VITE_API_URL?.replace(/\/$/, '') || 'https://api.v2.test.oddscomposer.io')
        console.log('SSE: Message published successfully:', result)
        return result
      } catch (error) {
        console.error('SSE: Failed to publish message:', error)
        throw error
      }
    },

    disconnectSSE() {
      sseService.disconnect()
      this.sseConnected = false
    },

    // Toggle auto-show notifications
    toggleAutoShowNotifications() {
      this.autoShowNotifications = !this.autoShowNotifications
    }
  }
}) 